import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Grid,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { CustomSelect } from "../../../../shared/components/inputfields/form-elements";
import { ActionsMenu } from "../../../../shared/components/table/custom-actions";
import CustomTable from "../../../../shared/components/table/custom-table";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { dtrackItService } from "../../../transaction-certificates/tc-service/dtrackit.service";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { useApi } from "../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";

const PageWrapper = styled("div")({
  padding: "0 25px 0px 25px",
  display: "grid",
  overflowY: "auto",
  flexDirection: "column",
  gap: "12px",
  flex: 1,
});

const ButtonContainer = styled(Box)({
  display: "flex",
  gap: "16px",
  marginBottom: "24px",
  alignItems: "center",
});

const columns = [
  { accessorKey: "teId", header: "Certified Organization TE-ID", size: 180 },
  { accessorKey: "coName", header: "Certified Organization Name", size: 220 },
  { accessorKey: "scNumber", header: "Scope Certificate Number", size: 220 },
  { accessorKey: "country", header: "Country", size: 140 },
  { accessorKey: "rawMaterial", header: "Raw Material", size: 220 },
  { accessorKey: "productCategory", header: "Product Category", size: 180 },
  { accessorKey: "productDetail", header: "Product Detail", size: 160 },
  { accessorKey: "lastUpdatedDate", header: "Last Updated Date", size: 150 },
  {
    accessorKey: "balance",
    header: "Certified Weight Available (in Kgs)",
    size: 200,
    Cell: ({ cell }: any) => {
      const val: number = cell.getValue();
      const color = val < 0 ? "#d32f2f" : val > 0 ? "#2e7d32" : "#555";
      return (
        <span style={{ color, fontWeight: 600 }}>
          {val != null
            ? val.toLocaleString(undefined, { maximumFractionDigits: 4 })
            : ""}
        </span>
      );
    },
  },
];

const obColumns = [
  { accessorKey: "teId", header: "Certified Organization TE-ID", size: 180 },
  { accessorKey: "coName", header: "Certified Organization Name", size: 220 },
  { accessorKey: "country", header: "Country", size: 140 },
  { accessorKey: "rawMaterial", header: "Raw Material", size: 220 },
  { accessorKey: "productCategory", header: "Product Category", size: 180 },
  { accessorKey: "productDetail", header: "Product Detail", size: 160 },
  { accessorKey: "transactionDate", header: "Opening Balance Transaction Date", size: 220 },
  {
    accessorKey: "openingBalance",
    header: "Certified Weight (in Kgs)",
    size: 180,
    Cell: ({ cell, row }: any) => {
      const val = cell.getValue();
      const isPending = val === "pending" || val == null;
      const num = isPending ? NaN : typeof val === "number" ? val : parseFloat(val);
      return (
        <span style={{ backgroundColor: isPending ? "#ffe0d6" : "transparent", padding: "4px 8px", display: "block" }}>
          {isPending ? "pending" : isNaN(num) ? val : num.toFixed(2)}
        </span>
      );
    },
  },
];

interface Filters {
  coName: string;
  teId: string;
  balance: string;
  rm: string;
  country: string[];
  pc: string;
  pd: string;
  dateFrom: string;
  dateTo: string;
  obSubmissionStatus: string;
}

const emptyFilters: Filters = {
  coName: "",
  teId: "",
  balance: "",
  rm: "",
  country: [],
  pc: "",
  pd: "",
  dateFrom: "",
  dateTo: "",
  obSubmissionStatus: "",
};

const obSubmissionOptions = [
  { value: "", label: "All" },
  { value: "Completed", label: "Completed" },
  { value: "Not Completed", label: "Not Completed" },
];

const balanceOptions = [
  { label: "All", value: "" },
  { label: "Positive", value: "positive" },
  { label: "Negative", value: "negative" },
  { label: "Zero", value: "zero" },
];

// Maps a ML Balance Summary API row to table row shape
// Supports both new flat camelCase format and old bracket-notation format
const mapMLRow = (item: any) => ({
  teId: item.teId ?? item["TE-ID"] ?? "",
  coName: item.coName ?? item["Certified Organization Name"] ?? item["Account Name"] ?? item["CO Name"] ?? "",
  scNumber: item.scNumber ?? item["SC Number"] ?? "",
  country: item.country ?? item.Country ?? "",
  rawMaterial: item.rawMaterial ?? item.RM ?? "",
  productCategory: item.productCategory ?? item.PC ?? "",
  productDetail: item.productDetail ?? item.PD ?? "",
  cbCode: item.cbCode ?? item.CB ?? item["CB Code"] ?? "",
  facilityType: item.facilityType ?? item["Facility Type"] ?? "",
  lastUpdatedDate: item.lastUpdatedDate ?? item["Last Updated Date"] ?? "",
  balance: item.balance ?? item.Balance ?? item["Balance (Kgs)"] ?? null,
  isNegative: item.isNegative ?? false,
  scValidUntil: item.scValidUntil ?? "",
});

// Maps an Opening Balance API row to table row shape
// Supports both new flat camelCase format and old bracket-notation format
const mapOBRow = (item: any) => ({
  teId: item.teId ?? item["TE-ID"] ?? "",
  coName: item.coName ?? item["Certified Organization Name"] ?? item["Account Name"] ?? item["CO Name"] ?? "",
  scNumber: item.scNumber ?? item["SC Number"] ?? "",
  country: item.country ?? item.Country ?? "",
  rawMaterial: item.rawMaterial ?? item.RM ?? "",
  productCategory: item.productCategory ?? item.PC ?? "",
  productDetail: item.productDetail ?? item.PD ?? "",
  cbCode: item.cbCode ?? item.CB ?? item["CB Code"] ?? "",
  transactionDate: item.obTransactionDate ?? item["Opening Balance Transaction Date"] ?? "",
  openingBalance: item.obBalance ?? item["Opening Balance"] ?? "pending",
  obSubmitted: item.obSubmitted ?? item["OB Submitted"] ?? "",
  standard: item.standard ?? "",
});

const MLQueryReport: React.FC = () => {
  const { cbLicensingCode } = useTransactionCertificateStore();
  const navigate = useNavigate();
  const { executeGet } = useApi();

  const [reportType, setReportType] = useState<"balance-summary" | "opening-balance">("opening-balance");

  const [filters, setFilters] = useState<Filters>({ ...emptyFilters });
  const appliedFiltersRef = useRef<Filters>({ ...emptyFilters });
  const hasAutoLoadedRef = useRef(false);

  const [rmOptions, setRmOptions] = useState<{ value: string; label: string }[]>([]);
  const [countryOptions, setCountryOptions] = useState<{ value: string; label: string }[]>([]);
  const [pcOptions, setPcOptions] = useState<{ value: string; label: string }[]>([]);
  const [pdOptions, setPdOptions] = useState<{ value: string; label: string }[]>([]);

  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [hasExplicitlySearched, setHasExplicitlySearched] = useState<boolean>(false);
  const [isResetEnabled, setResetEnable] = useState(false);
  const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [error, setError] = useState<string>("");
  const [resetKey, setResetKey] = useState<number>(0);

  // RM field disabled until TE-ID has a value
  const rmDisabled = !filters.teId.trim();

  // Load dropdown options on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [rmRes, pcRes, pdRes] = await Promise.allSettled([
          dtrackItService.getRawMaterialCodes(),
          dtrackItService.getProductCategory(),
          dtrackItService.getProductDetailsCode(),
        ]);
        if (rmRes.status === "fulfilled" && rmRes.value?.data) {
          setRmOptions(rmRes.value.data.map((t: any) => ({ value: `${t.description_1} (${t.code_1})`, label: `${t.description_1} (${t.code_1})` })));
        }
        if (pcRes.status === "fulfilled" && pcRes.value?.data) {
          setPcOptions(pcRes.value.data.map((t: any) => ({ value: `${t.description_1} (${t.code_1})`, label: `${t.description_1} (${t.code_1})` })));
        }
        if (pdRes.status === "fulfilled" && pdRes.value?.data) {
          setPdOptions(pdRes.value.data.map((t: any) => ({ value: `${t.description_1} (${t.code_1})`, label: `${t.description_1} (${t.code_1})` })));
        }
        try {
          const countryData = require("../../../../shared/data/country-codes.json");
          setCountryOptions(
            countryData
              .map((c: any) => ({ value: `${c.name} (${c.code})`, label: `${c.name} (${c.code})` }))
              .sort((a: any, b: any) => a.label.localeCompare(b.label))
          );
        } catch {
          setCountryOptions([]);
        }
      } catch (e) {
        console.error("Error loading filter options", e);
      }
    };
    load();
  }, []);

  // Auto-load data on mount with the logged-in user CB ID (matches ml-report pattern)
  useEffect(() => {
    if (cbLicensingCode && !hasAutoLoadedRef.current) {
      hasAutoLoadedRef.current = true;
      loadOpeningBalanceData();
    }
  }, [cbLicensingCode]); // eslint-disable-line

  // Initial load — no filters, just cb
  const loadInitialData = async () => {
    setIsLoading(true);
    setResetEnable(true);
    setError("");
    try {
      const queryParams: Record<string, any> = {
        cb: cbLicensingCode,
        limit: 10,
        offset: 0,
      };
      const response = await executeGet(TCAppConfig.ML_BALANCE_QUERY, queryParams);
      const rows = response?.data?.response ?? response?.data?.rows ?? [];
      setData(rows.map(mapMLRow));
      setTotal(response?.data?.no_of_records ?? response?.data?.total ?? rows.length);
      setHasSearched(true);
    } catch (err: any) {
      setError("Failed to load data. Please try again.");
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = useCallback(
    async (f: Filters, cb: string, pageIndex: number, pageSize: number) => {
      setIsLoading(true);
      setError("");
      try {
        const queryParams: Record<string, any> = { cb, limit: pageSize, offset: pageIndex * pageSize };
        const searchVal = f.teId.trim() || f.coName.trim();
        if (searchVal) queryParams.search = searchVal;
        if (f.balance) queryParams.balance = f.balance;
        if (f.rm) queryParams.rm = f.rm;
        if (f.country.length) queryParams.country = f.country.join(",");
        if (f.pc) queryParams.pc = f.pc;
        if (f.pd) queryParams.pd = f.pd;
        if (f.dateFrom) queryParams.dateFrom = f.dateFrom;
        if (f.dateTo) queryParams.dateTo = f.dateTo;
        const response = await executeGet(TCAppConfig.ML_BALANCE_QUERY, queryParams);
        const rows = response?.data?.response ?? response?.data?.rows ?? [];
        setData(rows.map(mapMLRow));
        setTotal(response?.data?.no_of_records ?? response?.data?.total ?? rows.length);
      } catch (err: any) {
        setError("Failed to load data. Please try again.");
        setData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [cbLicensingCode] // eslint-disable-line
  );

  const fetchObData = useCallback(
    async (f: Filters, cb: string, pageIndex: number, pageSize: number) => {
      setIsLoading(true);
      setError("");
      try {
        const queryParams: Record<string, any> = { cb, limit: pageSize, offset: pageIndex * pageSize };
        const searchVal = f.teId.trim() || f.coName.trim();
        if (searchVal) queryParams.search = searchVal;
        if (f.rm) queryParams.rm = f.rm;
        if (f.country.length) queryParams.country = f.country.join(",");
        if (f.pc) queryParams.pc = f.pc;
        if (f.pd) queryParams.pd = f.pd;
        if (f.obSubmissionStatus) {
          queryParams.status = f.obSubmissionStatus === "Completed" ? "Y" : "N";
        }
        const response = await executeGet(TCAppConfig.ML_OB_QUERY, queryParams);
        const rows = response?.data?.response ?? response?.data?.rows ?? [];
        setData(rows.map(mapOBRow));
        setTotal(response?.data?.no_of_records ?? response?.data?.total ?? rows.length);
      } catch (err: any) {
        setError("Failed to load opening balance data. Please try again.");
        setData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [cbLicensingCode] // eslint-disable-line
  );

  // Re-fetch on page change
  useEffect(() => {
    if (hasSearched) {
      if (reportType === "opening-balance") {
        fetchObData(appliedFiltersRef.current, cbLicensingCode, pagination.pageIndex, pagination.pageSize);
      } else {
        fetchData(appliedFiltersRef.current, cbLicensingCode, pagination.pageIndex, pagination.pageSize);
      }
    }
  }, [pagination]); // eslint-disable-line

  const handleSearch = () => {
    appliedFiltersRef.current = { ...filters };
    setHasSearched(true);
    setHasExplicitlySearched(true);
    setResetEnable(true);
    if (reportType === "opening-balance") {
      if (pagination.pageIndex === 0) {
        fetchObData(filters, cbLicensingCode, 0, pagination.pageSize);
      } else {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }
    } else {
      if (pagination.pageIndex === 0) {
        fetchData(filters, cbLicensingCode, 0, pagination.pageSize);
      } else {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }
    }
  };

  const handleReset = () => {
    setFilters({ ...emptyFilters });
    appliedFiltersRef.current = { ...emptyFilters };
    setData([]);
    setTotal(0);
    setHasSearched(false);
    setHasExplicitlySearched(false);
    setResetEnable(false);
    setError("");
    setResetKey((k) => k + 1);
    setPagination({ pageIndex: 0, pageSize: 10 });
    if (reportType === "opening-balance") {
      loadOpeningBalanceData();
    } else {
      loadInitialData();
    }
  };

  const loadOpeningBalanceData = async () => {
    setIsLoading(true);
    setResetEnable(true);
    setError("");
    try {
      const queryParams: Record<string, any> = {
        cb: cbLicensingCode,
        limit: 10,
        offset: 0,
      };
      const response = await executeGet(TCAppConfig.ML_OB_QUERY, queryParams);
      const rows = response?.data?.response ?? response?.data?.rows ?? [];
      setData(rows.map(mapOBRow));
      setTotal(response?.data?.no_of_records ?? response?.data?.total ?? rows.length);
      setHasSearched(true);
    } catch (err: any) {
      setError("Failed to load opening balance data. Please try again.");
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewRecord = (row: any) => {
    const teId = row.original.teId ?? "";
    const rmCode = row.original.rawMaterial ?? "";
    const pc = row.original.productCategory ?? "";
    const pd = row.original.productDetail ?? "";
    const params = new URLSearchParams();
    if (pc) params.set("pc", pc);
    if (pd) params.set("pd", pd);
    const query = params.toString();
    navigate(
      `/ml/material-ledger/${encodeURIComponent(teId)}/${encodeURIComponent(rmCode)}${query ? `?${query}` : ""}`
    );
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const f = appliedFiltersRef.current;
      const searchVal = f.teId.trim() || f.coName.trim();
      if (reportType === "opening-balance") {
        const queryParams: Record<string, any> = { cb: cbLicensingCode, format: "xlsx" };
        if (searchVal) queryParams.search = searchVal;
        if (f.rm) queryParams.rm = f.rm;
        if (f.country.length) queryParams.country = f.country.join(",");
        if (f.pc) queryParams.pc = f.pc;
        if (f.pd) queryParams.pd = f.pd;
        if (f.obSubmissionStatus) queryParams.status = f.obSubmissionStatus === "Completed" ? "Y" : "N";
        const response = await executeGet(TCAppConfig.ML_OB_EXPORT, queryParams, { responseType: "blob" });
        if (response?.data instanceof Blob) {
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = url;
          link.download = `ob-coverage-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
          link.click();
          window.URL.revokeObjectURL(url);
        }
      } else {
        const queryParams: Record<string, any> = { cb: cbLicensingCode, format: "xlsx" };
        if (searchVal) queryParams.search = searchVal;
        if (f.balance) queryParams.balance = f.balance;
        if (f.rm) queryParams.rm = f.rm;
        if (f.country.length) queryParams.country = f.country.join(",");
        if (f.pc) queryParams.pc = f.pc;
        if (f.pd) queryParams.pd = f.pd;
        if (f.dateFrom) queryParams.dateFrom = f.dateFrom;
        if (f.dateTo) queryParams.dateTo = f.dateTo;
        const response = await executeGet(TCAppConfig.ML_BALANCE_EXPORT, queryParams, { responseType: "blob" });
        if (response?.data instanceof Blob) {
          const url = window.URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = url;
          link.download = `ml-balance-summary-report-${new Date().toISOString().slice(0, 10)}.xlsx`;
          link.click();
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error("Export failed", err);
    } finally {
      setIsExporting(false);
    }
  };

  const setField = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };



  return (
    <PageWrapper>
      <Typography variant="h6" gutterBottom align="left" fontWeight="600">
        Material Ledger Report
      </Typography>

      <ButtonContainer>
        <StyledButton
          onClick={() => {
            setReportType("opening-balance");
            setData([]);
            setHasExplicitlySearched(false);
            setHasSearched(false);
            setResetEnable(false);
            setFilters({ ...emptyFilters });
            appliedFiltersRef.current = { ...emptyFilters };
            loadOpeningBalanceData();
          }}
          variant={reportType === "opening-balance" ? "contained" : "outlined"}
          color="warning"
          sx={{
            height: "40px",
            mb: 2,
            fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
            padding: { xs: "8px 12px", sm: "8px 16px", md: "8px 20px" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...(reportType === "opening-balance" ? {} : {
              backgroundColor: "transparent",
              borderColor: "#F5A623",
              color: "#333",
              "&:hover": { backgroundColor: "rgba(245, 166, 35, 0.04)" },
            }),
          }}
        >
          Opening Balance Coverage Report
        </StyledButton>
        <StyledButton
          onClick={() => {
            setReportType("balance-summary");
            setData([]);
            setHasExplicitlySearched(false);
            setHasSearched(false);
            setResetEnable(false);
            setFilters({ ...emptyFilters });
            appliedFiltersRef.current = { ...emptyFilters };
            loadInitialData();
          }}
          variant={reportType === "balance-summary" ? "contained" : "outlined"}
          color="warning"
          sx={{
            height: "40px",
            mb: 2,
            fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
            padding: { xs: "8px 12px", sm: "8px 16px", md: "8px 20px" },
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            ...(reportType === "balance-summary" ? {} : {
              backgroundColor: "transparent",
              borderColor: "#F5A623",
              color: "#333",
              "&:hover": { backgroundColor: "rgba(245, 166, 35, 0.04)" },
            }),
          }}
        >
          Material Ledger Balance Summary Report
        </StyledButton>
      </ButtonContainer>

      {/* Filters */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Search
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={filters.coName}
            placeholder="TE-ID, name, country"
            onChange={(e) => setField("coName", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Certified Organization TE-ID
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by TE-ID"
            value={filters.teId}
            onChange={(e) => setField("teId", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography
            variant="body2"
            fontWeight={500}
            mb={0.5}
            color={rmDisabled ? "text.disabled" : "text.primary"}
          >
            Select Raw Material Code
          </Typography>
          <CustomSelect
            size="small"
            options={rmOptions || []}
            value={filters.rm}
            disabled={rmDisabled}
            placeholder="Select Raw Material Code"
            enableSearch
            onChange={(e: any) => setField("rm", e?.target?.value ?? "")}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Country
          </Typography>
          <CustomSelect
            size="small"
            multiple
            options={countryOptions || []}
            value={filters.country}
            placeholder="Select country"
            enableSearch
            onChange={(e: any) => setField("country", Array.isArray(e?.target?.value) ? e.target.value : [])}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Product Category
          </Typography>
          <CustomSelect
            size="small"
            options={pcOptions || []}
            value={filters.pc}
            placeholder="Select Product Category"
            enableSearch
            onChange={(e: any) => setField("pc", e?.target?.value ?? "")}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Product Detail
          </Typography>
          <CustomSelect
            size="small"
            options={pdOptions || []}
            value={filters.pd}
            placeholder="Select Product Detail"
            enableSearch
            onChange={(e: any) => setField("pd", e?.target?.value ?? "")}
          />
        </Grid>

        {reportType === "opening-balance" && (
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Opening Balance Submission Status
          </Typography>
          <CustomSelect
            size="small"
            options={obSubmissionOptions}
            value={filters.obSubmissionStatus}
            placeholder="Select submission status"
            onChange={(e: any) => setField("obSubmissionStatus", e?.target?.value ?? "")}
          />
        </Grid>
        )}

        {reportType === "balance-summary" && <>
        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Last Updated Start Date
          </Typography>
          <TextField
            size="small"
            fullWidth
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setField("dateFrom", e.target.value)}
            inputProps={{ max: filters.dateTo || undefined }}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Last Updated End Date
          </Typography>
          <TextField
            size="small"
            fullWidth
            type="date"
            value={filters.dateTo}
            onChange={(e) => setField("dateTo", e.target.value)}
            inputProps={{ min: filters.dateFrom || undefined }}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={3}>
          <Typography variant="body2" fontWeight={500} mb={0.5}>
            Balance Type
          </Typography>
          <CustomSelect
            size="small"
            options={balanceOptions}
            value={filters.balance}
            placeholder="Select Balance Type"
            onChange={(e: any) => setField("balance", e?.target?.value ?? "")}
          />
        </Grid>
        </>}
      </Grid>

      <Alert severity="info" sx={{ mb: 2 }}>
        The data returned in the table/export is limited to the first 10,000 rows. Please apply
        filters to refine the data if more records exist.
      </Alert>

      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {/* Action row — matches ml-report layout */}
      <Grid
        item
        xs={12}
        sm={12}
        md={2}
        lg={2}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
      >
        <Typography variant="body2" sx={{ mr: "auto" }}>
          Showing first 10,000 rows of {total.toLocaleString()} total results.
        </Typography>
        <StyledButton
          onClick={handleExportData}
          variant="outlined"
          disabled={isExporting || isLoading || !hasExplicitlySearched || data.length === 0}
        >
          {isExporting ? "Exporting..." : "Export Data"}
        </StyledButton>
        <StyledButton onClick={handleSearch} disabled={isLoading}>
          {isLoading ? "Loading..." : "Search"}
        </StyledButton>
        <StyledButton
          onClick={handleReset}
          variant="outlined"
          disabled={!isResetEnabled}
        >
          Reset
        </StyledButton>
      </Grid>

      <CustomTable
        columns={reportType === "opening-balance" ? obColumns : columns}
        data={data}
        rowCount={total}
        isLoadingTodos={isLoading}
        paginations={pagination}
        setPaginations={setPagination}
        enablePagination={true}
        enableTopToolbar={true}
        enableBottomToolbar={true}
        handleSearchBar={false}
        enableStickyHeader={true}
        maxHeight="calc(100vh - 280px)"
        enableRowActions={true}
        renderRowActions={({ row }: any) => (
          <ActionsMenu
            row={row}
            onView={() => handleViewRecord(row)}
          />
        )}
      />
    </PageWrapper>
  );
};

export default MLQueryReport;
